import React, { useEffect, useRef, useState } from 'react';
import { BrainCircuitIcon, CheckIcon, CpuIcon, DropletsIcon, NetworkIcon, RepeatIcon } from 'lucide-react';

/* ------------------------------------------------------------------ */
/* Slide 1 — Three businesses, one platform                            */
/* ------------------------------------------------------------------ */

const platformLayers: Array<{
  id: string;
  index: string;
  name: string;
  kind: string;
  Icon: React.ElementType;
  items: string[];
  multiple: string;
  tone: 'base' | 'mid' | 'top';
}> = [{
  id: 'physical',
  index: '01',
  name: 'Physical network',
  kind: 'Connected hardware',
  Icon: CpuIcon,
  items: ['JuLi 5.0 deployment', 'Enterprise distribution', 'Connected IoT hardware', 'Installed nodes'],
  multiple: 'Hardware multiple',
  tone: 'base'
}, {
  id: 'recurring',
  index: '02',
  name: 'Recurring beverage platform',
  kind: 'Consumables + commerce',
  Icon: RepeatIcon,
  items: ['Cartridge revenue', 'Payments', 'Loyalty', 'Consumer identity', 'Fleet software'],
  multiple: 'Connected commerce multiple',
  tone: 'mid'
}, {
  id: 'intelligence',
  index: '03',
  name: 'Enterprise intelligence',
  kind: 'Software + data',
  Icon: BrainCircuitIcon,
  items: ['SIP', 'AI', 'Employee engagement', 'Wellness', 'Workplace analytics', 'HR software'],
  multiple: 'Software multiple',
  tone: 'top'
}];
const layerTone: Record<'base' | 'mid' | 'top', {
  shell: string;
  chip: string;
  badge: string;
  icon: string;
}> = {
  base: {
    shell: 'border-slate-300 bg-white',
    chip: 'border-slate-200 bg-slate-50 text-slate-600',
    badge: 'bg-slate-200 text-slate-700',
    icon: 'bg-slate-100 text-slate-600'
  },
  mid: {
    shell: 'border-blue-300 bg-blue-50',
    chip: 'border-blue-200 bg-white text-blue-800',
    badge: 'bg-blue-200 text-blue-900',
    icon: 'bg-blue-500 text-white'
  },
  top: {
    shell: 'border-navy-950 bg-navy-950',
    chip: 'border-white/20 bg-white/10 text-blue-50',
    badge: 'bg-blue-400 text-navy-950',
    icon: 'bg-blue-400 text-navy-950'
  }
};
export function ValuationLayersVisual() {
  return <section className="grid min-h-0 min-w-0 flex-1 gap-3 lg:grid-cols-[1.3fr_.7fr] lg:gap-5" aria-label="Three business layers stacked into one platform" data-id="element-1878" data-mp="2kg0w8v1pnonr">
      <ol className="deck-stagger flex min-h-0 min-w-0 flex-col gap-2.5" aria-label="Platform layers from hardware up to intelligence" data-id="element-1879" data-mp="2gh1j4n5tsll8">
        {platformLayers.map((layer, index) => {
        const tone = layerTone[layer.tone];
        const isTop = layer.tone === 'top';
        return <li key={layer.id} className={`deck-stagger-item pd3-val-layer relative flex min-h-0 min-w-0 flex-1 flex-col justify-center overflow-hidden rounded-2xl border-2 p-4 shadow-sm ${tone.shell}`} data-id="element-1880" data-mp="21qc8pn06vaif" data-mpi={index}>
              <span className={`pd3-val-sweep pointer-events-none absolute inset-y-0 -left-1/3 w-1/3 ${isTop ? 'bg-white/10' : 'bg-blue-500/10'}`} style={{
            animationDelay: `${index * 900}ms`
          }} aria-hidden="true" data-id="element-1881" data-mp="1nprnylapr4ma" />
              <div className="relative flex flex-wrap items-center gap-2.5" data-id="element-1882" data-mp="c8xd8uwh36m6">
                <span className={`pd3-val-layer-icon flex h-10 w-10 shrink-0 items-center justify-center rounded-xl ${tone.icon}`} style={{
              animationDelay: `${index * 420}ms`
            }} data-id="element-1883" data-mp="c6ue9ldce35b">
                  <layer.Icon className="h-5 w-5" aria-hidden="true" data-id="element-1884" data-mp="3goq7j2xgr2lm" />
                </span>
                <div className="min-w-0" data-id="element-1885" data-mp="3kpjqrizynt86">
                  <p className={`text-[10px] font-bold uppercase tracking-[.16em] ${isTop ? 'text-blue-300' : 'text-blue-700'}`} data-id="element-1886" data-mp="3pntp22gzf9y0">
                    Layer {layer.index} · {layer.kind}
                  </p>
                  <h3 className={`text-lg font-bold leading-tight sm:text-xl ${isTop ? 'text-white' : 'text-navy-950'}`} data-id="element-1887" data-mp="2wzfqnllzeblx">{layer.name}</h3>
                </div>
                <span className={`ml-auto shrink-0 rounded-full px-3 py-1.5 text-[10px] font-bold uppercase tracking-[.1em] ${tone.badge}`} data-id="element-1888" data-mp="31wfx3xmqtgdk">{layer.multiple}</span>
              </div>
              <ul className="relative mt-3 flex flex-wrap gap-1.5" data-id="element-1889" data-mp="13hqhsvs58f0s">
                {layer.items.map(item => <li key={item} className={`rounded-full border px-3 py-1.5 text-[11px] font-semibold ${tone.chip}`} data-id="element-1890" data-mp="1h952d71p8o9y">{item}</li>)}
              </ul>
            </li>;
      })}
      </ol>

      <aside className="flex min-h-0 min-w-0 flex-col gap-2.5" data-id="element-1891" data-mp="1r84e4yhrh016">
        <div className="relative flex flex-1 flex-col justify-center overflow-hidden rounded-2xl border-2 border-blue-200 bg-blue-50 p-4" data-id="element-1892" data-mp="1m5vks10z7vyj">
          <div className="pd3-val-rise absolute inset-x-0 bottom-0 h-full bg-gradient-to-t from-blue-500/20 to-transparent" aria-hidden="true" data-id="element-1893" data-mp="204dd6c1ttwms" />
          <p className="relative text-[10px] font-bold uppercase tracking-[.16em] text-blue-700" data-id="element-1894" data-mp="5rx0aseizk8y">Value per node</p>
          <p className="relative mt-2 text-base font-bold leading-snug text-navy-950" data-id="element-1895" data-mp="1sep4ocianjb">
            Each deployed machine becomes a recurring software, commerce, and intelligence platform — not simply a beverage dispenser.
          </p>
        </div>

        <div className="relative flex flex-1 flex-col justify-center overflow-hidden rounded-2xl bg-navy-950 p-4 text-white" data-id="element-1896" data-mp="3e2r5gryw7mgl">
          <p className="text-[10px] font-bold uppercase tracking-[.16em] text-blue-300" data-id="element-1897" data-mp="3j110n2kman27">Stacked value</p>
          <div className="mt-3 flex items-end gap-1.5" aria-hidden="true" data-id="element-1898" data-mp="344a7vc5g89lk">
            {[34, 62, 100].map((height, index) => <span key={height} className="flex flex-1 flex-col items-center gap-1.5" data-id="element-1899" data-mp="3957o3j6wetkf" data-mpi={index}>
                <span className="flex h-16 w-full items-end" data-id="element-1900" data-mp="2v56nm4ujpo5h">
                  <span className="pd3-val-column w-full rounded-t-md bg-blue-400" style={{
                ['--pd3-val-h' as string]: `${height}%`,
                animationDelay: `${index * 220}ms`
              }} data-id="element-1901" data-mp="302gbhg2adrr7" />
                </span>
                <span className="text-[9px] font-bold text-blue-200" data-id="element-1902" data-mp="yatrhygtax48">0{index + 1}</span>
              </span>)}
          </div>
          <p className="mt-3 text-sm font-bold leading-snug" data-id="element-1903" data-mp="rvl65a0zwj1v">
            Three revenue layers compound on the same install. One deployment, three businesses.
          </p>
        </div>
      </aside>
    </section>;
}

/* ------------------------------------------------------------------ */
/* Slide 2 — A hybrid platform deserves a hybrid multiple              */
/* ------------------------------------------------------------------ */

const comparables: Array<{
  company: string;
  business: string;
  multiple: string;
  value: number;
  tone: 'hardware' | 'connected' | 'software' | 'beverage';
}> = [{
  company: 'Block (Square)',
  business: 'Payment hardware + commerce',
  multiple: '~2.4×',
  value: 2.4,
  tone: 'hardware'
}, {
  company: 'Toast',
  business: 'Hardware + payments + SaaS',
  multiple: '~3×',
  value: 3,
  tone: 'hardware'
}, {
  company: 'Keurig Dr Pepper',
  business: 'Brewers + pods razor/blade',
  multiple: '~3.3×',
  value: 3.3,
  tone: 'beverage'
}, {
  company: 'SodaStream',
  business: 'Acquired by PepsiCo, 2018',
  multiple: '~5.9×',
  value: 5.9,
  tone: 'beverage'
}, {
  company: 'Nayax',
  business: 'Connected payments',
  multiple: '~6×',
  value: 6,
  tone: 'connected'
}, {
  company: 'Public SaaS',
  business: 'Software',
  multiple: '~7×',
  value: 7,
  tone: 'software'
}];
const smartSodaComposition: Array<{
  label: string;
  value: number;
  className: string;
}> = [{
  label: 'Hardware',
  value: 1.0,
  className: 'bg-slate-400'
}, {
  label: 'Connected',
  value: 1.2,
  className: 'bg-blue-400'
}, {
  label: 'Software',
  value: 1.6,
  className: 'bg-blue-500'
}, {
  label: 'Beverage',
  value: 1.0,
  className: 'bg-navy-950'
}];
const beveragePrecedents: Array<{
  name: string;
  context: string;
  stats: Array<[string, string]>;
  note: string;
}> = [{
  name: 'SodaStream',
  context: 'Acquired by PepsiCo · 2018 · $3.2B enterprise value',
  stats: [['Revenue', '~$543M'], ['EBITDA', '~$160M'], ['EV / Revenue', '~5.9×'], ['EV / EBITDA', '~20×']],
  note: 'The closest precedent — a strategic acquisition in the connected at-home beverage platform category.'
}, {
  name: 'Keurig Dr Pepper',
  context: 'Public company · ~$50B enterprise value',
  stats: [['Revenue', '~$15B'], ['EBITDA', '~$4.2B'], ['EV / Revenue', '~3.3×'], ['EV / EBITDA', '~12×']],
  note: 'The public benchmark for machine-plus-consumables — the brewer sells once, the pods sell forever.'
}];
const MAX_MULTIPLE = 7.5;
const SMARTSODA_MULTIPLE = 4.8;
export function ValuationMultipleVisual() {
  const [revealed, setRevealed] = useState(false);
  const frame = useRef<number | undefined>(undefined);
  useEffect(() => {
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
      setRevealed(true);
      return;
    }
    frame.current = window.setTimeout(() => setRevealed(true), 620);
    return () => window.clearTimeout(frame.current);
  }, []);
  useEffect(() => {
    const reveal = () => setRevealed(true);
    window.addEventListener('beforeprint', reveal);
    return () => window.removeEventListener('beforeprint', reveal);
  }, []);
  return <section className="grid min-h-0 min-w-0 flex-1 gap-3 lg:grid-cols-[1.15fr_.85fr] lg:gap-5" aria-label="Comparable revenue multiples and the resulting valuation" data-id="element-1904" data-mp="sx8n2y5mhnsm">
      <div className="flex min-h-0 min-w-0 flex-col" data-id="element-1905" data-mp="1knlsjbznsmg4">
        <div className="flex min-h-0 flex-1 flex-col rounded-2xl border border-slate-200 bg-white p-4 shadow-sm" data-id="element-1906" data-mp="1kowtl41pzg3j">
          <div className="flex flex-wrap items-center justify-between gap-2" data-id="element-1907" data-mp="1plx1lki4qsxa">
            <p className="text-[10px] font-bold uppercase tracking-[.16em] text-blue-700" data-id="element-1908" data-mp="1bm5jb7kkyqdh">Comparable revenue multiples</p>
            <div className="flex items-center gap-2.5 text-[9px] font-bold uppercase tracking-[.1em]" data-id="element-1909" data-mp="1gk72rtlx4rph">
              <span className="flex items-center gap-1.5 text-slate-500" data-id="element-1910" data-mp="9ux5k0cdsi0"><span className="h-2 w-4 rounded-full bg-slate-400" aria-hidden="true" data-id="element-1911" data-mp="57wfu3tuvpsy" />Hardware</span>
              <span className="flex items-center gap-1.5 text-blue-600" data-id="element-1912" data-mp="3mu4hk4nmxttu"><span className="h-2 w-4 rounded-full bg-blue-400" aria-hidden="true" data-id="element-1913" data-mp="3quna60xcr3x3" />Connected</span>
              <span className="flex items-center gap-1.5 text-blue-700" data-id="element-1914" data-mp="3d39rsqb6kc3n"><span className="h-2 w-4 rounded-full bg-blue-500" aria-hidden="true" data-id="element-1915" data-mp="3d2qly7yt1e5z" />Software</span>
              <span className="flex items-center gap-1.5 text-navy-950" data-id="element-1916" data-mp="3i0s5sv421hii"><span className="h-2 w-4 rounded-full bg-navy-950" aria-hidden="true" data-id="element-1917" data-mp="334kmry4dum4t" />Beverage platform</span>
            </div>
          </div>

          <ul className="mt-3 flex min-h-0 flex-1 flex-col justify-between gap-1.5" data-id="element-1918" data-mp="385qeptq7y5da">
            {comparables.map((comp, index) => {
            const width = comp.value / MAX_MULTIPLE * 100;
            const barTone = comp.tone === 'software' ? 'bg-blue-500' : comp.tone === 'connected' ? 'bg-blue-400' : comp.tone === 'beverage' ? 'bg-navy-950' : 'bg-slate-400';
            return <li key={comp.company} className="grid grid-cols-[96px_1fr_44px] items-center gap-2 sm:grid-cols-[150px_1fr_48px]" data-id="element-1919" data-mp="12gy517lxkl32" data-mpi={index}>
                  <div className="min-w-0" data-id="element-1920" data-mp="1g8mcuyp5cne8">
                    <p className="truncate text-xs font-bold text-navy-950" data-id="element-1921" data-mp="1gah0wlvpsvmj">{comp.company}</p>
                    <p className="truncate text-[10px] font-semibold text-slate-500" data-id="element-1922" data-mp="1bgcty2mg8nlg">{comp.business}</p>
                  </div>
                  <div className="relative h-6 overflow-hidden rounded-lg bg-slate-100" data-id="element-1923" data-mp="1l7ft2yvtisi3">
                    <span className={`pd3-val-bar absolute inset-y-0 left-0 rounded-lg ${barTone}`} style={{
                  ['--pd3-val-w' as string]: `${width}%`,
                  animationDelay: `${index * 110}ms`
                }} data-id="element-1924" data-mp="1l9tl0yv7bolf" />
                  </div>
                  <p className="text-right text-xs font-bold tabular-nums text-navy-950" data-id="element-1925" data-mp="4thc408ksyx9">{comp.multiple}</p>
                </li>;
          })}

            <li className="grid grid-cols-[96px_1fr_44px] items-center gap-2 border-t-2 border-blue-200 pt-2 sm:grid-cols-[150px_1fr_48px]" data-id="element-1926" data-mp="1zx5jxiwp87p4">
              <div className="min-w-0" data-id="element-1927" data-mp="24vp69kf7fli0">
                <p className="truncate text-sm font-bold text-blue-700" data-id="element-1928" data-mp="29utf29y7vh7t">SmartSoda</p>
                <p className="truncate text-[10px] font-semibold text-blue-600" data-id="element-1929" data-mp="2etmu46t5jga3">All four at once</p>
              </div>
              <div className="relative h-8 overflow-hidden rounded-lg border-2 border-blue-300 bg-blue-50" data-id="element-1930" data-mp="2ivrleyw4o0tk">
                <span className="pd3-val-bar absolute inset-y-0 left-0 flex overflow-hidden rounded-md" style={{
                ['--pd3-val-w' as string]: `${SMARTSODA_MULTIPLE / MAX_MULTIPLE * 100}%`,
                animationDelay: '820ms'
              }} data-id="element-1931" data-mp="2ntjdpu0opoya">
                  {smartSodaComposition.map(segment => <span key={segment.label} className={`flex items-center justify-center ${segment.className}`} style={{
                  width: `${segment.value / SMARTSODA_MULTIPLE * 100}%`
                }} title={`${segment.label} · ${segment.value.toFixed(1)}×`} data-id="element-1932" data-mp="32ksbwrinkin1">
                      <span className="truncate px-1 text-[8px] font-bold uppercase tracking-[.06em] text-white" data-id="element-1933" data-mp="lwiv32drh8eb">{segment.label}</span>
                    </span>)}
                </span>
              </div>
              <p className="text-right text-sm font-bold tabular-nums text-blue-700" data-id="element-1934" data-mp="1eker04viawa1">~4.8×</p>
            </li>
            <li className="pl-1 text-[9px] font-semibold leading-snug text-slate-500 sm:pl-[150px]" data-id="element-1935" data-mp="19nobpjrj04po">
              SmartSoda is not one of these businesses — it is all four, so the multiple is built from each layer it actually operates.
            </li>
          </ul>
        </div>

        <div className="mt-2.5 grid gap-2 sm:grid-cols-2" data-id="element-1936" data-mp="1xc7itvyuabg6">
          {beveragePrecedents.map(precedent => <article key={precedent.name} className="deck-stagger-item rounded-xl border-2 border-navy-950 bg-navy-950 px-3 py-2.5 text-white" data-id="element-1937" data-mp="1sk69aht22ytu">
              <div className="flex items-baseline justify-between gap-2" data-id="element-1938" data-mp="1d5kg3x81uo2j">
                <p className="text-xs font-bold" data-id="element-1939" data-mp="1h64e130g5vpq">{precedent.name}</p>
                <span className="shrink-0 rounded-full bg-blue-400 px-2 py-0.5 text-[9px] font-bold text-navy-950" data-id="element-1940" data-mp="1361znqrclkf8">Beverage precedent</span>
              </div>
              <p className="mt-0.5 text-[9px] font-semibold text-slate-400" data-id="element-1941" data-mp="1vtpfytll5ow2">{precedent.context}</p>
              <dl className="mt-2 grid grid-cols-4 gap-1.5" data-id="element-1942" data-mp="20ssmkih7s365">
                {precedent.stats.map(([label, value]) => <div key={label} className="rounded-lg bg-white/5 px-1.5 py-1 text-center" data-id="element-1943" data-mp="1m4ebjk9n98ei">
                    <dt className="text-[8px] font-bold uppercase tracking-[.06em] text-slate-400" data-id="element-1944" data-mp="1r2fs9yjlkvmt">{label}</dt>
                    <dd className="mt-0.5 text-[10px] font-bold tabular-nums leading-tight text-blue-100" data-id="element-1945" data-mp="2esa36akcwa4f">{value}</dd>
                  </div>)}
              </dl>
              <p className="mt-1.5 text-[9px] leading-snug text-slate-300" data-id="element-1946" data-mp="841e8vregobi">{precedent.note}</p>
            </article>)}
        </div>

        <div className="mt-2 grid gap-2 sm:grid-cols-3" data-id="element-1947" data-mp="34ezuizu5214">
          {[['Below pure SaaS', 'We do not yet earn a software multiple.'], ['Under the SodaStream line', 'The closest strategic precedent cleared ~5.9× revenue.'], ['Priced on proof', 'Anchored to what is delivered, not projected.']].map(([title, detail]) => <div key={title} className="rounded-xl border border-blue-200 bg-blue-50 px-3 py-2" data-id="element-1948" data-mp="3gc9oq8o7rk2x">
              <p className="text-[10px] font-bold uppercase tracking-[.12em] text-blue-700" data-id="element-1949" data-mp="3uc10e2r9codr">{title}</p>
              <p className="mt-1 text-[11px] leading-snug text-slate-600" data-id="element-1950" data-mp="3pbwpqtd16nvg">{detail}</p>
            </div>)}
        </div>
      </div>

      <div className="flex min-h-0 min-w-0 flex-col justify-between rounded-3xl border-2 border-navy-950 bg-navy-950 p-4 text-white sm:p-5" data-id="element-1951" data-mp="19jph3yyyn372">
        <p className="text-[10px] font-bold uppercase tracking-[.16em] text-blue-300" data-id="element-1952" data-mp="291ffx7j8n7b">SmartSoda today</p>
        <dl className="mt-2 flex flex-1 flex-col justify-center gap-1.5" data-id="element-1953" data-mp="77m89bkgg4lq">
          <div className="rounded-xl bg-white/5 px-3.5 py-3" data-id="element-1954" data-mp="79p6ujzob622">
            <dt className="text-[10px] font-semibold uppercase tracking-[.12em] text-slate-300" data-id="element-1955" data-mp="bar3qu64b60v">2026 revenue</dt>
            <dd className="mt-0.5 text-2xl font-bold tabular-nums" data-id="element-1956" data-mp="g7rcjcucqmvo">~$15.7M</dd>
          </div>
          <p className="text-center text-xl font-bold leading-none text-blue-300" aria-hidden="true" data-id="element-1957" data-mp="3fp8vt6tba2bb">×</p>
          <div className="rounded-xl bg-white/5 px-3.5 py-3" data-id="element-1958" data-mp="3jpixw8dzg8kw">
            <dt className="text-[10px] font-semibold uppercase tracking-[.12em] text-slate-300" data-id="element-1959" data-mp="3oo43yofx1mh9">Blended multiple</dt>
            <dd className="mt-0.5 text-2xl font-bold tabular-nums" data-id="element-1960" data-mp="3tfuf2wmoge9q">~4.8×</dd>
          </div>
          <p className="text-center text-xl font-bold leading-none text-blue-300" aria-hidden="true" data-id="element-1961" data-mp="2vyn97k4tpthv">=</p>
          <div className={`rounded-xl border-2 border-blue-400 bg-blue-500 px-3 py-4 text-center transition-all duration-700 ${revealed ? 'scale-100 opacity-100' : 'scale-90 opacity-0'}`} data-id="element-1962" data-mp="30xgoni3o7uko">
            <dt className="text-[10px] font-bold uppercase tracking-[.14em] text-blue-50" data-id="element-1963" data-mp="2oh7bxlpp9787">Pre-money valuation</dt>
            <dd className="mt-1 text-4xl font-bold tabular-nums leading-none" data-id="element-1964" data-mp="2jfhwltvg27eb">$75M</dd>
          </div>
        </dl>
        <p className="mt-3 text-[11px] leading-snug text-blue-100" data-id="element-1965" data-mp="2ff7uisarw14q">
          SmartSoda sits between connected hardware and enterprise software — and meaningfully below the SodaStream precedent in its own category.
        </p>
        <p className="mt-2 border-t border-white/15 pt-2 text-[9px] leading-snug text-slate-400" data-id="element-1966" data-mp="20ql3htbs9kxo">
          SodaStream figures per PepsiCo acquisition disclosure (2018). Public-company multiples reflect reported financials and prevailing market data; shown for context only.
        </p>
      </div>
    </section>;
}

/* ------------------------------------------------------------------ */
/* Funding history — every round priced against progress              */
/* ------------------------------------------------------------------ */

type FundingRound = {
  id: string;
  years: string;
  round: string;
  raised: string;
  valuation: string;
  valuationValue: number;
  milestone?: string;
  phase: 'discovery' | 'development' | 'scaling';
  future?: boolean;
};
const fundingRounds: FundingRound[] = [{
  id: '2019',
  years: '2019',
  round: 'Founding round',
  raised: '$1.1M',
  valuation: '$10M',
  valuationValue: 10,
  milestone: 'Smart Soda Holdings Inc. founded',
  phase: 'discovery'
}, {
  id: '2020',
  years: '2020',
  round: 'Seed',
  raised: '$680K',
  valuation: '$10M',
  valuationValue: 10,
  phase: 'discovery'
}, {
  id: '2021',
  years: '2021',
  round: 'Series A',
  raised: '$1.3M',
  valuation: '$10M',
  valuationValue: 10,
  milestone: 'Contracts signed with key partners',
  phase: 'discovery'
}, {
  id: '2022',
  years: '2022',
  round: 'Series B',
  raised: '$3.1M',
  valuation: '$35M',
  valuationValue: 35,
  milestone: 'Strategic investor',
  phase: 'development'
}, {
  id: '2023',
  years: '2023',
  round: 'F&F round',
  raised: '$1.5M',
  valuation: '$55M',
  valuationValue: 55,
  milestone: 'JuLi™ 1.0 launch',
  phase: 'development'
}, {
  id: '2024',
  years: '2024–26',
  round: 'Series C',
  raised: '$1.8M',
  valuation: '$75M',
  valuationValue: 75,
  milestone: '$10M round',
  phase: 'development'
}, {
  id: '2026',
  years: '2026',
  round: 'Series C',
  raised: '$5M',
  valuation: '$75M',
  valuationValue: 75,
  milestone: 'JuLi™ 5.0 · Launching SIP',
  phase: 'scaling',
  future: true
}];
const phaseTone: Record<FundingRound['phase'], {
  dot: string;
  label: string;
  text: string;
}> = {
  discovery: {
    dot: 'bg-slate-400',
    label: 'Discovery / recovery',
    text: 'text-slate-400'
  },
  development: {
    dot: 'bg-blue-400',
    label: 'Development',
    text: 'text-blue-300'
  },
  scaling: {
    dot: 'bg-blue-300',
    label: 'Scaling',
    text: 'text-blue-200'
  }
};
const MAX_VALUATION = 75;
export function FundingHistoryVisual() {
  return <section className="flex min-w-0 flex-1 flex-col justify-center" aria-label="Funding history and valuation progression since 2019" data-id="element-1967" data-mp="2qzn2nnbfzbp2">
      <div className="relative min-w-0 rounded-3xl border border-white/15 bg-white/5 p-3 sm:p-4" data-id="element-1968" data-mp="2zje26jxi57li">
        <div className="flex flex-wrap items-center justify-between gap-2" data-id="element-1969" data-mp="3dj5nl5qclq9j">
          <p className="text-[10px] font-bold uppercase tracking-[.16em] text-blue-300" data-id="element-1970" data-mp="3dg9m57fakjgc">Every round priced against delivered progress</p>
          <div className="flex flex-wrap items-center gap-2.5" data-id="element-1971" data-mp="2kt6vt2krrf16">
            {(['discovery', 'development', 'scaling'] as const).map(phase => <span key={phase} className="flex items-center gap-1.5 text-[9px] font-bold uppercase tracking-[.1em] text-slate-300" data-id="element-1972" data-mp="2fsslfdvp3szz">
                <span className={`h-1.5 w-4 rounded-full ${phaseTone[phase].dot}`} aria-hidden="true" data-id="element-1973" data-mp="2psjf5dcfm3z0" />
                {phaseTone[phase].label}
              </span>)}
          </div>
        </div>

        <ol className="deck-stagger mt-3 grid min-w-0 gap-2 sm:grid-cols-4 lg:grid-cols-7" aria-label="Financing rounds by year" data-id="element-1974" data-mp="1x4vw42qt7leh">
          {fundingRounds.map(round => {
          const tone = phaseTone[round.phase];
          const height = round.valuationValue ? Math.max(12, round.valuationValue / MAX_VALUATION * 100) : 0;
          return <li key={round.id} className={`deck-stagger-item flex min-w-0 flex-col rounded-2xl border p-2.5 ${round.future ? 'border-blue-400 bg-blue-500/15' : 'border-white/12 bg-navy-950/50'}`} data-id="element-1975" data-mp="3jkdgsayf2i3">
                <div className="flex items-center justify-between gap-1" data-id="element-1976" data-mp="10c61r9o5fmtz">
                  <p className={`text-[11px] font-bold ${round.future ? 'text-blue-200' : 'text-white'}`} data-id="element-1977" data-mp="mceqhgp0okjo">{round.years}</p>
                  <span className={`h-1.5 w-1.5 shrink-0 rounded-full ${tone.dot}`} aria-hidden="true" data-id="element-1978" data-mp="rag9k1mg0jv5" />
                </div>

                <div className="mt-2 flex h-14 items-end" aria-hidden="true" data-id="element-1979" data-mp="370srf12k9i7j">
                  {round.valuationValue ? <span className={`pd3-val-column w-full rounded-t-md ${round.future ? 'bg-blue-400' : 'bg-blue-500/60'}`} style={{
                ['--pd3-val-h' as string]: `${height}%`
              }} data-id="element-1980" data-mp="3bz3zp7d7jknn" /> : <span className="h-[2px] w-full rounded-full bg-white/15" data-id="element-1981" data-mp="17tvtb7vcp2f5" />}
                </div>

                <p className={`mt-2 text-[10px] font-bold leading-tight ${round.future ? 'text-white' : 'text-blue-100'}`} data-id="element-1982" data-mp="13rr2si078lwq">{round.round}</p>
                {round.valuationValue > 0 && <p className="mt-1 text-sm font-bold tabular-nums leading-none text-white" data-id="element-1983" data-mp="tucksdymnp2b">{round.valuation}</p>}
                {round.raised !== '—' && <p className={`mt-0.5 text-[9px] font-semibold uppercase tracking-[.1em] ${round.future ? 'text-blue-200' : 'text-slate-400'}`} data-id="element-1984" data-mp="kw0vqgdeszbz">
                    {round.future ? 'Raising' : 'Raised'} {round.raised}
                  </p>}
                {round.milestone && <p className="mt-1.5 text-[9px] leading-snug text-slate-300" data-id="element-1985" data-mp="11r5uqffrb61">{round.milestone}</p>}
              </li>;
        })}
        </ol>
      </div>

      <div className="mt-3 grid min-w-0 gap-2.5 lg:grid-cols-[1.1fr_.9fr]" data-id="element-1986" data-mp="5xzk81lt70c5">
        <div className="rounded-2xl border-2 border-blue-400 bg-blue-500 p-3.5 text-white" data-id="element-1987" data-mp="arua5ms0cbjo">
          <p className="text-[10px] font-bold uppercase tracking-[.16em] text-blue-50" data-id="element-1988" data-mp="es4i34zd1c16">Current funding round</p>
          <p className="mt-1.5 text-lg font-bold leading-tight" data-id="element-1989" data-mp="3e9ub4hz5bgns">Raising $5M in Series C at a $75M pre-money valuation.</p>
          <p className="mt-1 text-[11px] font-semibold text-blue-50" data-id="element-1990" data-mp="3n8fpe0xl49ir">$10M raised to date across six years of building.</p>
        </div>
        <div className="rounded-2xl border border-white/15 bg-navy-950/50 p-3.5" data-id="element-1991" data-mp="3s7sl7b6foqxh">
          <p className="text-[10px] font-bold uppercase tracking-[.16em] text-blue-300" data-id="element-1992" data-mp="12iqbpr8matrn">Projected liquidity event</p>
          <div className="mt-2 flex items-center gap-2" data-id="element-1993" data-mp="17ga19narmsyw">
            {['2027', '2028–30'].map((year, index) => <React.Fragment key={year} data-id="element-1994" data-mp="160xnf82uuogu" data-mpi={index}>
                {index > 0 && <span className="h-px flex-1 bg-blue-400/50" aria-hidden="true" data-id="element-1995" data-mp="111b7urzk0w4v" />}
                <span className="rounded-full border border-blue-300/50 px-2.5 py-1 text-[10px] font-bold text-blue-200" data-id="element-1996" data-mp="1f0uethgmq1fl">{year}</span>
              </React.Fragment>)}
            <span className="h-px flex-1 bg-blue-400/50" aria-hidden="true" data-id="element-1997" data-mp="1axxthizpfr7y" />
            <span className="rounded-full bg-blue-400 px-2.5 py-1 text-[10px] font-bold text-navy-950" data-id="element-1998" data-mp="1oxp91o5vdfo2">IPO target</span>
          </div>
          <p className="mt-2 text-[10px] leading-snug text-slate-400" data-id="element-1999" data-mp="1jy2xdj5ow7hd">Illustrative long-term scenario only. There is no guarantee any specific outcome will be achieved; past performance is not indicative of future results.</p>
        </div>
      </div>
    </section>;
}

/* ------------------------------------------------------------------ */
/* Slide 3 — How we earn higher valuations                             */
/* ------------------------------------------------------------------ */

const valuationStages: Array<{
  id: string;
  when: string;
  value: string;
  status: string;
  provenLabel: string;
  proven: string[];
  toProve: string[];
  parts: Array<{
    label: string;
    Icon: React.ElementType;
  }>;
  equationNote: string;
  tone: 'now' | 'next' | 'later';
}> = [{
  id: 'today',
  when: 'Today',
  value: '$75M',
  status: 'Supported by the business as it exists',
  provenLabel: 'Already proven',
  proven: ['Revenue', 'Deployments', 'Gross margin'],
  toProve: [],
  parts: [{
    label: 'Hardware',
    Icon: CpuIcon
  }, {
    label: 'Consumables',
    Icon: DropletsIcon
  }],
  equationNote: 'Two revenue lines, valued additively',
  tone: 'now'
}, {
  id: 'next',
  when: 'Next',
  value: '$125–150M',
  status: 'Earned through the next set of milestones',
  provenLabel: 'Must prove',
  proven: [],
  toProve: ['SIP ARR', 'Paid transactions', 'Consumables growth', 'Customer retention', 'Enterprise customers'],
  parts: [{
    label: 'Hardware',
    Icon: CpuIcon
  }, {
    label: 'Consumables',
    Icon: DropletsIcon
  }, {
    label: 'Technology',
    Icon: BrainCircuitIcon
  }],
  equationNote: 'Software on top of the pour changes the multiple, not just the total',
  tone: 'next'
}, {
  id: 'later',
  when: 'Later',
  value: '$250M+',
  status: 'Earned at platform scale',
  provenLabel: 'Must prove',
  proven: [],
  toProve: ['Thousands of deployed nodes', 'High software mix', 'Network effects', 'International scale', 'Predictable cash flow'],
  parts: [{
    label: 'All three, networked',
    Icon: NetworkIcon
  }],
  equationNote: 'Every endpoint makes every other endpoint worth more',
  tone: 'later'
}];
export function ValuationRoadmapVisual() {
  return <section className="flex min-w-0 flex-1 flex-col justify-center" aria-label="How higher valuations are earned over time" data-id="element-2000" data-mp="2pv5sv2mmqvw0">
      <div className="relative" data-id="element-2001" data-mp="2ktqaxjfr15hu">
        <span className="pd3-val-track absolute left-0 right-0 top-[26px] hidden h-[3px] rounded-full bg-blue-200 lg:block" aria-hidden="true" data-id="element-2002" data-mp="10d8zzakim7q9" />
        <ol className="deck-stagger relative grid min-w-0 gap-3 lg:grid-cols-3" data-id="element-2003" data-mp="15cvdzm863s04">
          {valuationStages.map(stage => {
          const isNow = stage.tone === 'now';
          const isLater = stage.tone === 'later';
          return <li key={stage.id} className={`deck-stagger-item flex min-w-0 flex-col rounded-2xl border-2 p-4 ${isNow ? 'border-blue-500 bg-navy-950 text-white shadow-lg' : isLater ? 'border-dashed border-slate-300 bg-white' : 'border-blue-200 bg-blue-50'}`} data-id="element-2004" data-mp="1j3gxnd5g45z2">
                <div className="flex items-center gap-2" data-id="element-2005" data-mp="1x2zsj498xktf">
                  <span className={`pd3-val-node flex h-[26px] w-[26px] shrink-0 items-center justify-center rounded-full border-2 ${isNow ? 'border-blue-300 bg-blue-500 text-white' : isLater ? 'border-slate-300 bg-white text-slate-400' : 'border-blue-400 bg-white text-blue-600'}`} data-id="element-2006" data-mp="1x2glwjp1qiup">
                    <span className="h-2 w-2 rounded-full bg-current" aria-hidden="true" data-id="element-2007" data-mp="3fwt8f3u4gkwu" />
                  </span>
                  <p className={`text-[10px] font-bold uppercase tracking-[.18em] ${isNow ? 'text-blue-300' : 'text-blue-700'}`} data-id="element-2008" data-mp="3az17jkbi1ogi">{stage.when}</p>
                </div>

                <p className={`mt-3 text-3xl font-bold leading-none tabular-nums ${isNow ? 'text-white' : 'text-navy-950'}`} data-id="element-2009" data-mp="3kq5m4ix9j7at">{stage.value}</p>
                <p className={`mt-1.5 text-[11px] font-semibold leading-snug ${isNow ? 'text-blue-100' : 'text-slate-500'}`} data-id="element-2010" data-mp="2x1mf06py90kb">{stage.status}</p>

                {/* What the business is made of at this stage */}
                <div className={`mt-3 rounded-xl border px-2.5 py-2 ${isNow ? 'border-white/15 bg-white/5' : isLater ? 'border-slate-200 bg-slate-50' : 'border-blue-200 bg-white'}`} data-id="element-2011" data-mp="3113iiultfnmu">
                  <div className="flex flex-wrap items-center gap-x-1.5 gap-y-1" data-id="element-2012" data-mp="lc24fuz57x1k">
                    {stage.parts.map((part, partIndex) => <React.Fragment key={part.label} data-id="element-2013" data-mp="orn7f8ur766y" data-mpi={partIndex}>
                        {partIndex > 0 && <span className={`text-[11px] font-bold ${isNow ? 'text-blue-300' : 'text-blue-500'}`} aria-hidden="true" data-id="element-2014" data-mp="stqk20t5sgtt">
                            +
                          </span>}
                        <span className={`flex items-center gap-1 rounded-lg px-1.5 py-1 ${isNow ? 'bg-white/10 text-blue-100' : isLater ? 'bg-white text-navy-950' : 'bg-blue-50 text-navy-950'}`} data-id="element-2015" data-mp="jtle7v3m5qhr">
                          <part.Icon className={`h-3 w-3 shrink-0 ${isNow ? 'text-blue-300' : 'text-blue-600'}`} aria-hidden="true" data-id="element-2016" data-mp="xsl1aybh9xbt" />
                          <span className="text-[9px] font-bold leading-none" data-id="element-2017" data-mp="12qmkrkctfynt">{part.label}</span>
                        </span>
                      </React.Fragment>)}
                  </div>

                  <p className={`mt-1.5 text-[9px] leading-snug ${isNow ? 'text-slate-400' : 'text-slate-500'}`} data-id="element-2018" data-mp="1v8qs7v973bgo">{stage.equationNote}</p>
                </div>

                <p className={`mt-3 text-[9px] font-bold uppercase tracking-[.14em] ${isNow ? 'text-blue-300' : 'text-slate-400'}`} data-id="element-2019" data-mp="326t1qtqs3z0a">{stage.provenLabel}</p>
                <ul className="mt-1.5 space-y-1.5" data-id="element-2020" data-mp="36ykt36hre8rh">
                  {stage.proven.map(item => <li key={item} className="flex items-center gap-2 text-[11px] font-bold text-white" data-id="element-2021" data-mp="3bw2qq0qbt1k6">
                      <span className="flex h-4 w-4 shrink-0 items-center justify-center rounded-full bg-blue-400 text-navy-950" data-id="element-2022" data-mp="2eb864v7oryxn">
                        <CheckIcon className="h-2.5 w-2.5" aria-hidden="true" data-id="element-2023" data-mp="2o7jkdqqu6yuq" />
                      </span>
                      {item}
                    </li>)}
                  {stage.toProve.map(item => <li key={item} className="flex items-center gap-2 text-[11px] font-semibold text-navy-950" data-id="element-2024" data-mp="3g1021r9ayvm">
                      <span className={`h-1.5 w-1.5 shrink-0 rounded-full ${isLater ? 'bg-slate-300' : 'bg-blue-400'}`} aria-hidden="true" data-id="element-2025" data-mp="7h2zail6baxl" />
                      {item}
                    </li>)}
                </ul>
              </li>;
        })}
        </ol>
      </div>

      <p className="mt-4 rounded-2xl bg-blue-600 px-4 py-3 text-center text-sm font-bold leading-snug text-white sm:text-base" data-id="element-2026" data-mp="1j6tqtuosnwny">
        Today’s valuation is supported by today’s business. Tomorrow’s valuation must be earned through execution — not projected into existence.
      </p>
    </section>;
}